import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Skeleton from "@material-ui/lab/Skeleton";

import TableHeader, { TableHeaderButton } from "src/components/TableHeader";
import type { BankAccountType } from "src/interfaces/bank-account.interface";
import type { Reconciliation } from "src/interfaces/reconciliation.interface";
import { updateBankAccountVisibility } from "src/apiService/modules/bank-accounts";
import { checkAndUpdateReconciliations } from "src/apiService/modules/reconciliations";
import { bankAccountsSelector } from "src/store/bankAccount/selector";
import { getTourOpen } from "src/store/tour/selector";
import { userRoleSelector } from "src/store/system/selector";
import type { BankAccountWithReconciliations } from "./types";

import BankAccount from "./BankAccount";
import AddBankAccount from "./AddBankAccount";
import Expandable from "./Expandable";

const LoadingPlaceholders = [0, 1] as const;

const useStyles = makeStyles((theme) => ({
  container: {
    "& > * + *": {
      marginTop: theme.spacing(1),
    },
  },
  expandable: {
    "& > * + *": {
      marginTop: theme.spacing(1),
    },
    "& > *:first-child": {
      marginTop: theme.spacing(-1),
    },
  },
}));

function Content() {
  const classes = useStyles();
  const userRole = useSelector(userRoleSelector);
  const tourOpen = useSelector(getTourOpen);
  const bankAccountsFromStore = useSelector(bankAccountsSelector);
  const [bankAccounts, setBankAccounts] = useState<
    BankAccountWithReconciliations[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<
    | {
        type: "bank-account";
        id: string;
      }
    | {
        type: "bank-account";
        bankAccountType: BankAccountType;
      }
    | null
  >(null);

  const updateBankAccountReconciliation = useCallback(
    (nextReconciliation: Reconciliation) => {
      setBankAccounts((previous) =>
        previous.map((bankAccount) => {
          if (bankAccount.id !== nextReconciliation.bank_account_id) {
            return bankAccount;
          }
          const index = bankAccount.reconciliations.findIndex(
            (reconciliation) => reconciliation.id === nextReconciliation.id
          );
          const nextReconciliations =
            index >= 0
              ? bankAccount.reconciliations.map((reconciliation) =>
                  reconciliation.id === nextReconciliation.id
                    ? { ...reconciliation, ...nextReconciliation }
                    : reconciliation
                )
              : [...bankAccount.reconciliations, nextReconciliation];
          nextReconciliations.sort((a, b) => a.date.localeCompare(b.date));
          return {
            ...bankAccount,
            reconciliations: nextReconciliations,
          };
        })
      );
    },
    []
  );

  const loadBankAccounts = useCallback(async () => {
    const response = await checkAndUpdateReconciliations();
    setBankAccounts(response?.bankAccounts || []);
  }, []);

  useEffect(() => {
    if (tourOpen) {
      setBankAccounts(
        bankAccountsFromStore as BankAccountWithReconciliations[]
      );
      setLoading(false);
      return;
    }
    let canceled = false;
    const loadData = async () => {
      try {
        await loadBankAccounts();
      } catch (error) {
        console.error("Error checking and updating reconciliations:", error);
        toast.error("Failed to update reconciliations");
      } finally {
        if (canceled) return;
        setLoading(false);
      }
    };
    loadData();
    return () => {
      canceled = true;
    };
  }, [bankAccountsFromStore, loadBankAccounts, tourOpen]);
  const {
    salePlatforms,
    salePlatformsHidden,
    bankCreditAccounts,
    bankCreditAccountsHidden,
  } = useMemo(() => {
    const all = [...bankAccounts];
    all.sort((a, b) => a.name.localeCompare(b.name));
    return all.reduce(
      (all, bankAccount) => {
        if (
          bankAccount.type === "bank-account" ||
          bankAccount.type === "credit-card"
        ) {
          if (bankAccount.hidden)
            all.bankCreditAccountsHidden.push(bankAccount);
          else all.bankCreditAccounts.push(bankAccount);
        } else {
          if (bankAccount.hidden) all.salePlatformsHidden.push(bankAccount);
          else all.salePlatforms.push(bankAccount);
        }
        return all;
      },
      {
        salePlatforms: [] as BankAccountWithReconciliations[],
        salePlatformsHidden: [] as BankAccountWithReconciliations[],
        bankCreditAccounts: [] as BankAccountWithReconciliations[],
        bankCreditAccountsHidden: [] as BankAccountWithReconciliations[],
      }
    );
  }, [bankAccounts]);

  return (
    <>
      <Box
        component={Paper}
        display="flex"
        flexDirection="column"
        minHeight={730}
        height="auto"
        p={2.5}
        mt={2}
        className="reconciliation-container"
      >
        <TableHeader title="Sale Platforms">
          <TableHeaderButton
            color="primary"
            disabled={userRole === "viewer"}
            onClick={() => {
              setDialog({
                type: "bank-account",
                bankAccountType: "sale-platform" as const,
              });
            }}
          >
            Add Account
          </TableHeaderButton>
        </TableHeader>
        <div className={classes.container}>
          {loading && salePlatforms.length === 0
            ? LoadingPlaceholders.map((placeholder) => (
                <Skeleton
                  key={`sale-loading-${placeholder}`}
                  variant="rect"
                  width="100%"
                  height={200}
                />
              ))
            : null}
          {salePlatforms.map((a) =>
            loading ? (
              <Skeleton key={a.id} variant="rect" width="100%" height={200} />
            ) : (
              <BankAccount
                key={a.id}
                bankAccount={a}
                onEdit={() => {
                  setDialog({
                    type: "bank-account",
                    id: a.id,
                  });
                }}
                onVisibility={async () => {
                  if (a.integration_id) {
                    toast.error(
                      `Bank accounts with an active integration attached can't be hidden`
                    );
                    return;
                  }
                  await updateBankAccountVisibility(a.id, {
                    hidden: !a.hidden,
                  });
                  setBankAccounts((previous) =>
                    previous.map((item) =>
                      item.id === a.id ? { ...item, hidden: !a.hidden } : item
                    )
                  );
                }}
                onReconciliationUpdated={updateBankAccountReconciliation}
              />
            )
          )}
          {salePlatformsHidden.length > 0 ? (
            <Expandable>
              <div className={classes.expandable}>
                {salePlatformsHidden.map((a) =>
                  loading ? (
                    <Skeleton
                      key={a.id}
                      variant="rect"
                      width="100%"
                      height={200}
                    />
                  ) : (
                    <BankAccount
                      key={a.id}
                      bankAccount={a}
                      onEdit={() => {
                        setDialog({
                          type: "bank-account",
                          id: a.id,
                        });
                      }}
                      onVisibility={async () => {
                        if (a.integration_id) {
                          toast.error(
                            `Bank accounts with an active integration attached can't be hidden`
                          );
                          return;
                        }
                        await updateBankAccountVisibility(a.id, {
                          hidden: !a.hidden,
                        });
                        setBankAccounts((previous) =>
                          previous.map((item) =>
                            item.id === a.id
                              ? { ...item, hidden: !a.hidden }
                              : item
                          )
                        );
                      }}
                      onReconciliationUpdated={updateBankAccountReconciliation}
                    />
                  )
                )}
              </div>
            </Expandable>
          ) : null}
        </div>
        <Box mt={2} width="100%" />
        <TableHeader title="Bank Accounts & Credit Cards">
          <TableHeaderButton
            color="primary"
            disabled={userRole === "viewer"}
            onClick={() => {
              setDialog({
                type: "bank-account",
                bankAccountType: "bank-account" as const,
              });
            }}
          >
            Add Account
          </TableHeaderButton>
        </TableHeader>
        <div className={classes.container}>
          {loading && bankCreditAccounts.length === 0
            ? LoadingPlaceholders.map((placeholder) => (
                <Skeleton
                  key={`bank-loading-${placeholder}`}
                  variant="rect"
                  width="100%"
                  height={200}
                />
              ))
            : null}
          {bankCreditAccounts.map((a) =>
            loading ? (
              <Skeleton key={a.id} variant="rect" width="100%" height={200} />
            ) : (
              <BankAccount
                key={a.id}
                bankAccount={a}
                onEdit={() => {
                  setDialog({
                    type: "bank-account",
                    id: a.id,
                  });
                }}
                onVisibility={async () => {
                  await updateBankAccountVisibility(a.id, {
                    hidden: !a.hidden,
                  });
                  setBankAccounts((previous) =>
                    previous.map((item) =>
                      item.id === a.id ? { ...item, hidden: !a.hidden } : item
                    )
                  );
                }}
                onReconciliationUpdated={updateBankAccountReconciliation}
              />
            )
          )}
          {bankCreditAccountsHidden.length > 0 ? (
            <Expandable>
              <div className={classes.expandable}>
                {bankCreditAccountsHidden.map((a) =>
                  loading ? (
                    <Skeleton
                      key={a.id}
                      variant="rect"
                      width="100%"
                      height={200}
                    />
                  ) : (
                    <BankAccount
                      key={a.id}
                      bankAccount={a}
                      onEdit={() => {
                        setDialog({
                          type: "bank-account",
                          id: a.id,
                        });
                      }}
                      onVisibility={async () => {
                        await updateBankAccountVisibility(a.id, {
                          hidden: !a.hidden,
                        });
                        setBankAccounts((previous) =>
                          previous.map((item) =>
                            item.id === a.id
                              ? { ...item, hidden: !a.hidden }
                              : item
                          )
                        );
                      }}
                      onReconciliationUpdated={updateBankAccountReconciliation}
                    />
                  )
                )}
              </div>
            </Expandable>
          ) : null}
        </div>
      </Box>
      <AddBankAccount
        open={dialog?.type === "bank-account"}
        id={
          dialog?.type === "bank-account" && "id" in dialog
            ? dialog.id
            : undefined
        }
        type={
          dialog?.type === "bank-account" && "bankAccountType" in dialog
            ? dialog.bankAccountType
            : undefined
        }
        onClose={async () => {
          setDialog(null);
          try {
            await loadBankAccounts();
          } catch (error) {
            console.error("Error reloading reconciliations:", error);
          }
        }}
      />
    </>
  );
}

export default Content;
